/**
 * ContextualChat — a collapsible side-pane wrapper around AgentsChat.
 *
 * Mounted on Gantt / BOM / Approvals / Design so the user can ask the
 * matching specialist (Rohan / Riaan / Dev / Neeraj) without leaving
 * the surface. Pre-routes the FIRST exchange to the contextual agent;
 * subsequent messages use the full keyword router (so "actually about
 * budget…" still hits Riaan even from the Gantt panel).
 *
 * Default collapsed — a single chip floats at the top-right. Expanded
 * it occupies a fixed-width column the parent grid leaves space for.
 * Persists open/closed in sessionStorage keyed by the surface id so
 * the user's choice carries across tab switches.
 */
import { useEffect, useState } from "react";
import { MessageSquare, ChevronRight, ChevronLeft } from "lucide-react";
import { AgentsChat } from "./AgentsChat";
import { resolveActor } from "../../lib/agent/timeline";
import { getAgent } from "../../lib/agent/orchestrator";

export interface ContextualChatProps {
  /** Stable id used for the open/closed sessionStorage key. */
  readonly surfaceId: string;
  /** Which specialist to pre-route the first message to.
   *  Suggested prompts + persona title are read from the agent's
   *  registry row (codegen.json#agents), NOT passed inline. */
  readonly defaultAgentId: string;
}

const SS_KEY_PREFIX = "foyrmind.contextualChat.";

export function ContextualChat(props: ContextualChatProps): JSX.Element {
  const { surfaceId, defaultAgentId } = props;
  // Pull persona-specific prompts + title from the typed agent
  // registry (codegen.json#agents) so the surface call site stays
  // tight — no literal strings to maintain here.
  const agentRow = getAgent(defaultAgentId);
  const suggestedPrompts = (agentRow as { suggestedPrompts?: ReadonlyArray<string> } | undefined)?.suggestedPrompts;
  const headline = agentRow ? `${agentRow.expert.replace(/-/g, " ")} · ${agentRow.label}` : undefined;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try { return window.sessionStorage.getItem(SS_KEY_PREFIX + surfaceId) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.sessionStorage.setItem(SS_KEY_PREFIX + surfaceId, open ? "1" : "0"); }
    catch { /* ignore */ }
  }, [surfaceId, open]);

  const persona = resolveActor("agent", defaultAgentId);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border border-border bg-cream hover:border-foyr/40 hover:bg-cream-dark/40 transition shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
        title={`Ask ${persona.displayName} — ${persona.title}`}
      >
        <span
          className="w-6 h-6 rounded-full grid place-items-center text-[9.5px] font-semibold text-cream shrink-0"
          style={{ background: persona.color }}
        >
          {persona.initials}
        </span>
        <span className="text-[11.5px] text-ink-950 font-medium">Ask {persona.displayName}</span>
        <span className="text-[10.5px] text-ink-500 hidden sm:inline">{persona.title}</span>
        <ChevronLeft className="w-3 h-3 text-ink-500" />
      </button>
    );
  }

  return (
    <aside className="rounded-2xl border border-border/70 bg-cream overflow-hidden">
      <header className="flex items-center justify-between px-3 py-2 border-b border-border/60 bg-cream-dark/30">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-6 h-6 rounded-full grid place-items-center text-[9.5px] font-semibold text-cream shrink-0"
            style={{ background: persona.color }}
          >
            {persona.initials}
          </span>
          <div className="min-w-0">
            <div className="text-[11.5px] font-medium text-ink-950 leading-tight">{persona.displayName}</div>
            <div className="text-[10px] text-ink-500 leading-tight truncate">{headline ?? persona.title}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-1 rounded-md hover:bg-cream-dark text-ink-500 hover:text-ink-950 transition"
          title="Collapse"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </header>
      <div className="p-2">
        <AgentsChat
          compact
          defaultAgentId={defaultAgentId}
          {...(suggestedPrompts !== undefined ? { suggestedPrompts } : {})}
        />
      </div>
    </aside>
  );
}

// Unused import suppression
void MessageSquare;
